// @flow

import * as R from 'ramda';

import { HttpService } from '~/services';
import {
  getRouteNavigationParamsWithFormattedDate,
  renameKeys,
} from '~/helpers';

export const resolvers = {
  Query: {
    voucherSalesHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ) => {
      const parseValue = (value) => {
        const parsedValue = parseFloat(value);

        return Number.isNaN(parsedValue) ? value : parsedValue;
      };

      const columnsMapping = {
        DateTimeUtc: 'date',
        Sales: 'sales',
        SalesIncome: 'salesIncome',
        SalesNetIncome: 'salesNetIncome',
        SalesType: 'salesType',
        DownlineUsername: 'downlineUsername',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          sales: ['SalesFrom', 'SalesTo'],
          salesIncome: ['SalesIncomeFrom', 'SalesIncomeTo'],
          salesNetIncome: ['SalesNetIncomeFrom', 'SalesNetIncomeTo'],
          salesType: 'SalesType',
          downlineUserName: 'DownlineUsername',
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get list
      const {
        data: { SalesList, PageCount = 1, TotalSalesIncome },
      } = await HttpService.client.get(
        '/MyWealth/DepositReward/GetVoucherSalesTrxList',
        { params },
      );

      const items = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            sales: parseValue,
            salesIncome: parseValue,
            salesNetIncome: parseValue,
          }),
          R.assoc('__typename', 'VoucherSalesHistoryItem'),
        ),
        SalesList,
      );

      const voucherSalesHistory = {
        data: items,
        pages: PageCount,
        totalSalesIncome: parseFloat(TotalSalesIncome),
        __typename: 'VoucherSalesHistory',
      };

      cache.writeData({
        data: { voucherSalesHistory },
        variables: props,
      });

      return voucherSalesHistory;
    },
    superOrderIncomeHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?SuperOrderIncomeHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        VaultCapacity: 'capacity',
        SopIncome: 'amount',
        NetIncome: 'netAmount',
        VaultExcess: 'vault',
        LockedEarnings: 'locked',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          capacity: ['VaultCapacityFrom', 'VaultCapacityTo'],
          amount: ['SopIncomeFrom', 'SopIncomeTo'],
          netAmount: ['NetIncomeFrom', 'NetIncomeTo'],
          vault: ['VaultExcessFrom', 'VaultExcessTo'],
          locked: ['LockedEarningFrom', 'LockedEarningTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get list
      const {
        data: {
          SopIncomeList,
          PageCount = 1,
          TotalSopIncome,
          TotalNetIncome,
          VaultCapacity,
          TotalVaultExcess,
          TotalLockedEarnings,
        },
      } = await HttpService.client.get(
        '/MyWealth/DepositReward/GetSopIncomeTrxList',
        { params },
      );

      const items: SuperOrderIncomeHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            capacity: parseFloat,
            amount: parseFloat,
            netAmount: parseFloat,
            vault: parseFloat,
            locked: parseFloat,
          }),
          R.assoc('__typename', 'SuperOrderIncomeHistoryItem'),
        ),
        SopIncomeList,
      );

      const superOrderIncomeHistory = {
        data: items,
        pages: PageCount,
        totalSuperOrderIncome: parseFloat(TotalSopIncome),
        totalNetIncome: parseFloat(TotalNetIncome),
        totalCapacity: parseFloat(VaultCapacity),
        totalVault: parseFloat(TotalVaultExcess),
        totalLocked: parseFloat(TotalLockedEarnings),
        __typename: 'SuperOrderIncomeHistory',
      };

      cache.writeData({
        data: { superOrderIncomeHistory },
        variables: props,
      });

      return superOrderIncomeHistory;
    },
    useIncomeHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?UseIncomeHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        PlatformName: 'platformName',
        MerchantName: 'merchantName',
        OrderID: 'orderId',
        UseQuantity: 'quantity',
        UseIncome: 'amount',
        UseNetIncome: 'netAmount',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          platformId: 'PlatformId',
          merchantName: 'MerchantName',
          orderId: 'OrderID',
          quantity: ['UseQuantityFrom', 'UseQuantityTo'],
          amount: ['UseIncomeFrom', 'UseIncomeTo'],
          netAmount: ['UseNetIncomeFrom', 'UseNetIncomeTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      const {
        data: { UseIncomeList, PageCount = 1, TotalUseIncome },
      } = await HttpService.client.get(
        '/MyWealth/DepositReward/GetUseIncomeTrxList',
        { params },
      );

      const items: UseIncomeHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            quantity: parseFloat,
            amount: parseFloat,
            netAmount: parseFloat,
          }),
          R.assoc('__typename', 'UseIncomeHistoryItem'),
        ),
        UseIncomeList,
      );

      const useIncomeHistory = {
        data: items,
        pages: PageCount,
        totalUseIncome: parseFloat(TotalUseIncome),
        __typename: 'UseIncomeHistory',
      };

      cache.writeData({
        data: { useIncomeHistory },
        variables: props,
      });

      return useIncomeHistory;
    },
    retailIncomeHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?RetailIncomeHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        RetailQuantity: 'quantity',
        RetailIncome: 'amount',
        RetailNetIncome: 'netAmount',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          quantity: ['RetailQuantityFrom', 'RetailQuantityTo'],
          amount: ['RetailIncomeFrom', 'RetailIncomeTo'],
          netAmount: ['RetailNetIncomeFrom', 'RetailNetIncomeTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      const {
        data: { RetailIncomeList, PageCount = 1, TotalRetailIncome },
      } = await HttpService.client.get(
        '/MyWealth/DepositReward/GetRetailIncomeTrxList',
        { params },
      );

      const items: RetailIncomeHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            quantity: parseFloat,
            amount: parseFloat,
            netAmount: parseFloat,
          }),
          R.assoc('__typename', 'RetailIncomeHistoryItem'),
        ),
        RetailIncomeList,
      );

      const retailIncomeHistory = {
        data: items,
        pages: PageCount,
        totalRetailIncome: parseFloat(TotalRetailIncome),
        __typename: 'RetailIncomeHistory',
      };

      cache.writeData({
        data: { retailIncomeHistory },
        variables: props,
      });

      return retailIncomeHistory;
    },
  },
};
